import axios from 'axios'
import type { CustomParamsSerializer } from 'axios'
import { useStoreLogger } from '../logger.ts'
import { stringify } from './JSV.ts'
import { redactSecret } from '../utils/redactSecret'
import {
  GetBearerToken,
  GetApiKey,
  GetCultureName,
  GetWebConnectorUrl,
  GetOrderEntryConnectorUrl,
  GetWebConnectorToken,
  GetLogoutUrl,
  GetDebugLoggingId,
} from '../stores/SettingsStore.ts'
import type { IPwsDynamicReportExecuteRequest, IOrderTypeCountryRequest, IOrderTypePoePriceCatRequest } from '../types/CommonTypes.ts'
import { ErrorCategory, type IAppError } from '../types/ErrorTypes.ts'

export const apiClient = axios.create()

apiClient.interceptors.response.use(
  response => response,
  async error => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status

      if (status === 401) {
        const logoutUrl = await GetLogoutUrl()
        // empty/self logout URL would reload this page and loop on every request
        if (logoutUrl && logoutUrl !== window.location.href) {
          window.location.href = logoutUrl
        }
        return Promise.reject(error)
      }

      if (!error.response) {
        const appError: IAppError = {
          category: ErrorCategory.Network,
          message: error.message,
          originalError: error,
        }
        return Promise.reject(appError)
      }

      if (status !== undefined && status >= 500) {
        const appError: IAppError = {
          category: ErrorCategory.Server,
          code: String(status),
          message: error.message,
          originalError: error,
        }
        return Promise.reject(appError)
      }
    }
    return Promise.reject(error)
  },
)

let dateFormatLocale: string | null = null

export function setDateFormatLocale(locale: string | null | undefined) {
  dateFormatLocale = locale && locale.length > 0 ? locale : null
}

export function getDateFormatLocale(): string {
  return dateFormatLocale || navigator.language || 'en-US'
}

export async function ApiPost(url: string, data: unknown) {
  const logger = useStoreLogger()
  const postUrl = url
  const bearerToken = await GetBearerToken()
  const apiKey = await GetApiKey()
  const cultureName = await GetCultureName()
  const debugLoggingId = await GetDebugLoggingId()

  const config = {
    headers: {
      Authorization: null as string | null,
      'x-api-key': `${apiKey}`,
      'Accept-Language': null as string | null,
      'X-Debug-Logging-Id': null as string | null,
    },
  }
  if (bearerToken) {
    logger.debug('bearerToken:', redactSecret(bearerToken))
    config.headers.Authorization = `Bearer ${bearerToken}`
  }
  if (cultureName) {
    logger.debug('cultureName:', cultureName)
    config.headers['Accept-Language'] = cultureName
  }
  if (debugLoggingId) {
    config.headers['X-Debug-Logging-Id'] = debugLoggingId
  }
  logger.info(postUrl, data)

  const response = await apiClient.post(postUrl, data, config)
  logger.info(response)
  if (response.headers['content-type']?.startsWith('application/json')) {
    return response.data
  }

  return null
}

export async function ApiPostWithLargeIntegerSupport(url: string, data: unknown) {
  const logger = useStoreLogger()
  const postUrl = url
  const bearerToken = await GetBearerToken()
  const apiKey = await GetApiKey()
  const cultureName = await GetCultureName()
  const debugLoggingId = await GetDebugLoggingId()

  const config = {
    headers: {
      Authorization: null as string | null,
      'x-api-key': `${apiKey}`,
      'Accept-Language': null as string | null,
      'X-Debug-Logging-Id': null as string | null,
    },
    transformResponse: [
      // First transformer: handle raw string response
      (data: string) => {
        if (typeof data === 'string') {
          // Replace large integers in GUID fields with quoted strings before JSON parsing
          let modifiedData = data.replace(
            /"beeDocumentGuid":(\d{16,})/g,
            '"beeDocumentGuid":"$1"'
          )
          modifiedData = modifiedData.replace(
            /"cusWalletGuid":(\d{16,})/g,
            '"cusWalletGuid":"$1"'
          )
          return modifiedData
        }
        return data
      },
      // Second transformer: parse JSON normally
      (data: string) => {
        if (typeof data === 'string') {
          try {
            return JSON.parse(data)
          } catch {
            return data
          }
        }
        return data
      }
    ],
  }
  if (bearerToken) {
    logger.debug('bearerToken:', redactSecret(bearerToken))
    config.headers.Authorization = `Bearer ${bearerToken}`
  }
  if (cultureName) {
    logger.debug('cultureName:', cultureName)
    config.headers['Accept-Language'] = cultureName
  }
  if (debugLoggingId) {
    config.headers['X-Debug-Logging-Id'] = debugLoggingId
  }
  logger.info(postUrl, data)

  const response = await apiClient.post(postUrl, data, config)
  logger.info(response)
  if (response.headers['content-type']?.startsWith('application/json')) {
    return response.data
  }

  return null
}

function JsvParamsSerializer(params: Record<string, never>): string {
  const logger = useStoreLogger()
  logger.info('params:', params)
  const serializedParams = [] as string[]

  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value != null) {
      serializedParams.push(`${key}=${stringify(value)}`)
    }
  })

  const paramsString = serializedParams.join('&')
  logger.info('serializedParams:', paramsString)
  return paramsString
}

export async function ApiGet(url: string, params: unknown | null = null) {
  const logger = useStoreLogger()
  const requestUrl = url
  const bearerToken = await GetBearerToken()
  const apiKey = await GetApiKey()
  const cultureName = await GetCultureName()
  const webConnectorToken = await GetWebConnectorToken()
  const debugLoggingId = await GetDebugLoggingId()

  const config = {
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'x-api-key': `${apiKey}`,
      'Accept-Language': null as string | null,
      'x-WebConnector-Token': `${webConnectorToken}`,
      'X-Debug-Logging-Id': null as string | null,
    },
    params: params,
    paramsSerializer: JsvParamsSerializer as CustomParamsSerializer,
  }

  if (cultureName) {
    logger.debug('cultureName:', cultureName)
    config.headers['Accept-Language'] = cultureName
  }
  if (debugLoggingId) {
    config.headers['X-Debug-Logging-Id'] = debugLoggingId
  }

  logger.info(
    'requestUrl:',
    requestUrl,
    'bearerToken:',
    redactSecret(bearerToken),
    'apiKey:',
    redactSecret(apiKey),
    'cultureName:',
    cultureName,
    'webConnectorToken:',
    redactSecret(webConnectorToken)
  )
  const response = await apiClient.get(requestUrl, config)
  logger.info(response)
  if (response.headers['content-type']?.startsWith('application/json')) {
    return response.data
  }
  return null
}

export async function ApiDelete(url: string, data: unknown | null = null) {
  const logger = useStoreLogger()
  const deleteUrl = url
  const bearerToken = await GetBearerToken()
  const apiKey = await GetApiKey()
  const cultureName = await GetCultureName()
  const debugLoggingId = await GetDebugLoggingId()

  const config = {
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'x-api-key': `${apiKey}`,
      'Accept-Language': null as string | null,
      'X-Debug-Logging-Id': null as string | null,
    },
    data: data,
  }

  if (cultureName) {
    logger.debug('cultureName:', cultureName)
    config.headers['Accept-Language'] = cultureName
  }
  if (debugLoggingId) {
    config.headers['X-Debug-Logging-Id'] = debugLoggingId
  }

  logger.info(deleteUrl, data)
  const response = await apiClient.delete(deleteUrl, config)
  logger.info(response)
  if (response.headers['content-type']?.startsWith('application/json')) {
    return response.data
  }
  return null
}

export async function ApiPut(url: string, data: unknown) {
  const logger = useStoreLogger()
  const postUrl = url
  const bearerToken = await GetBearerToken()
  const apiKey = await GetApiKey()
  const cultureName = await GetCultureName()
  const debugLoggingId = await GetDebugLoggingId()

  const config = {
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'x-api-key': `${apiKey}`,
      'Accept-Language': null as string | null,
      'X-Debug-Logging-Id': null as string | null,
    },
  }

  if (cultureName) {
    logger.debug('cultureName:', cultureName)
    config.headers['Accept-Language'] = cultureName
  }
  if (debugLoggingId) {
    config.headers['X-Debug-Logging-Id'] = debugLoggingId
  }

  logger.info(postUrl, data)
  const response = await apiClient.put(postUrl, data, config)
  logger.info(response)
  if (response.headers['content-type']?.startsWith('application/json')) {
    return response.data
  }
  return null
}

export async function ApiPostStream(url: string, file: File, queryParams: Record<string, string> = {}) {
  const logger = useStoreLogger()
  const bearerToken = await GetBearerToken()
  const apiKey = await GetApiKey()
  const cultureName = await GetCultureName()
  const debugLoggingId = await GetDebugLoggingId()

  // Add query parameters to URL
  const urlWithParams = new URL(url)
  Object.entries(queryParams).forEach(([key, value]) => {
    urlWithParams.searchParams.append(key, value)
  })

  const config = {
    headers: {
      Authorization: null as string | null,
      'x-api-key': `${apiKey}`,
      'Accept-Language': null as string | null,
      'Content-Type': file.type || 'application/octet-stream',
      'X-Debug-Logging-Id': null as string | null,
    },
  }

  if (bearerToken) {
    logger.debug('bearerToken:', redactSecret(bearerToken))
    config.headers.Authorization = `Bearer ${bearerToken}`
  }

  if (cultureName) {
    logger.debug('cultureName:', cultureName)
    config.headers['Accept-Language'] = cultureName
  }
  if (debugLoggingId) {
    config.headers['X-Debug-Logging-Id'] = debugLoggingId
  }

  logger.info(urlWithParams.toString(), 'File stream upload')

  const response = await apiClient.post(urlWithParams.toString(), file, config)
  logger.info(response)
  if (response.headers['content-type']?.startsWith('application/json')) {
    return response.data
  }

  return null
}

export function formatDateWithShortMonth(dateToBeFormatted: string): string {
  const date = new Date(dateToBeFormatted)
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }
  return date.toLocaleString(getDateFormatLocale(), options)
}

export function formatDateWithShortMonthAndTime(
  dateToBeFormatted: string | Date | undefined | null,
): string {
  if (!dateToBeFormatted) {
    return ''
  }
  const date = new Date(dateToBeFormatted)
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }
  return date.toLocaleString(getDateFormatLocale(), options)
}

export function formatDate(dateToBeFormatted: string | Date | undefined | null): string {
  if (!dateToBeFormatted) {
    return ''
  }
  const date = new Date(dateToBeFormatted)
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }

  return date.toLocaleString(getDateFormatLocale(), options)
}

export function formatDateAsDDMMYYYY(dateToBeFormatted: string | Date | undefined | null): string {
  if (!dateToBeFormatted) {
    return ''
  }
  const date = new Date(dateToBeFormatted)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export function formatDateTimeForJSONRequest(dateToBeFormatted: Date): string {
  const formatedDate =
    dateToBeFormatted.getFullYear() +
    '-' +
    (dateToBeFormatted.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    dateToBeFormatted.getDate().toString().padStart(2, '0') +
    'T' +
    dateToBeFormatted.getHours().toString().padStart(2, '0') +
    ':' +
    dateToBeFormatted.getMinutes().toString().padStart(2, '0') +
    ':' +
    dateToBeFormatted.getSeconds().toString().padStart(2, '0')
  return formatedDate
}

export function formatDateForJSONRequest(dateToBeFormatted: Date): string {
  const formatedDate =
    dateToBeFormatted.getFullYear() +
    '-' +
    (dateToBeFormatted.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    dateToBeFormatted.getDate().toString().padStart(2, '0')
  return formatedDate
}


export async function Post(url: string, data: unknown) {
  const logger = useStoreLogger()
  const postUrl = url

  logger.info(postUrl, data)

  const response = await axios.post(postUrl, data)
  logger.info(response)
  return response.data
}
export async function Get(url: string) {

  const logger = useStoreLogger()
  const getUrl = url
  logger.info(getUrl)
  const response = await axios.get(getUrl)

  return response.data
}

export async function PwsDynamicReportExecute(reportRequest: IPwsDynamicReportExecuteRequest) {
  const logger = useStoreLogger()
  logger.debug('PwsDynamicReportExecute', reportRequest)
  const url = await GetWebConnectorUrl('PwsDynamicReportExecute')
  return ApiGet(url, reportRequest)
}

export async function OrderTypeCountryGet(otCountryRequest: IOrderTypeCountryRequest) {
  const url = await GetOrderEntryConnectorUrl('OrderTypeCountryGet')
  return ApiGet(url, otCountryRequest)
}

export async function OrderTypePoePriceCatGet(otPoePriceCatRequest: IOrderTypePoePriceCatRequest) {
  const url = await GetOrderEntryConnectorUrl('OrderTypePoePriceCatGetList')
  return ApiGet(url, otPoePriceCatRequest)
}

